import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import {
  FeatureDefinition,
  featuresDefinition,
  signaler,
  SignalerFeatureFlagName
} from '~/adapters/signaler/signalerFeatureFlags';
import {ga4event} from '~/utils/analytics';

interface SignalerContextType {
  version: number;
  onFeatureFlagUpdate: () => void;
}

const SignalerContext = createContext<SignalerContextType | null>(null);

/*
The purpose of this provider is to track feature flags in GA and to provide a way to rerender whole React app when feature flag is manually changed in /signaler page
*/
export const SignalerProvider: React.FC<{children: ReactNode}> = ({
  children
}) => {
  // track all signaler feature flags that are running an AB test
  useEffect(() => {
    Object.entries(featuresDefinition).forEach(
      ([featureName, featureDefinition]) => {
        const value = signaler.featureFlag(
          featureName as SignalerFeatureFlagName
        );
        const flags =
          featuresDefinition[featureName as SignalerFeatureFlagName]?.flags;
        const isABTestRunning = Object.values(flags).every(
          value => value !== 1
        );
        if (!isABTestRunning) {
          return;
        }
        if (value) {
          ga4event({
            event: 'ab_test_group',
            ab_test_name: `${(featureDefinition as FeatureDefinition).launchDate ? (featureDefinition as FeatureDefinition).launchDate + ' ' : ''}${featureName}`,
            ab_test_location: 'lab',
            ab_test_group: value
          });
        }
      }
    );
  }, []);

  const [version, setVersion] = useState(0);

  const rerenderApp = useCallback(() => {
    setVersion(prev => {
      return prev + 1;
    });
  }, [setVersion]);

  return (
    <SignalerContext.Provider
      value={{version, onFeatureFlagUpdate: rerenderApp}}>
      {children}
    </SignalerContext.Provider>
  );
};

export const useSignalerContext = () => {
  const context = useContext(SignalerContext);
  if (!context) {
    throw new Error(
      'useSignalerContext must be used within a SignalerProvider'
    );
  }
  return context;
};
